import React, { createContext, useContext, useReducer } from 'react';
import { UserRole, Language, ViewType, LandRecord, Notification, FilterState, Document } from '../data/types';
import { landRecords as initialLandRecords, notifications as initialNotifications, allDocuments } from '../data/seedData';
interface AppState {
  currentRole: UserRole;
  language: Language;
  currentView: ViewType;
  selectedRecordId: string | null;
  landRecords: LandRecord[];
  notifications: Notification[];
  documents: Document[];
  filters: FilterState;
  sidebarCollapsed: boolean;
  searchQuery: string;
}
type AppAction = {
  type: 'SET_ROLE';
  payload: UserRole;
} | {
  type: 'SET_LANGUAGE';
  payload: Language;
} | {
  type: 'SET_VIEW';
  payload: ViewType;
} | {
  type: 'SET_SELECTED_RECORD';
  payload: string | null;
} | {
  type: 'SET_FILTERS';
  payload: FilterState;
} | {
  type: 'CLEAR_FILTERS';
} | {
  type: 'TOGGLE_SIDEBAR';
} | {
  type: 'SET_SEARCH_QUERY';
  payload: string;
} | {
  type: 'ADD_LAND_RECORD';
  payload: LandRecord;
} | {
  type: 'UPDATE_LAND_RECORD';
  payload: LandRecord;
} | {
  type: 'DELETE_LAND_RECORD';
  payload: string;
} | {
  type: 'ADD_DOCUMENT';
  payload: Document;
} | {
  type: 'ADD_LITIGATION';
  payload: {
    recordId: string;
    litigation: LandRecord['litigation'][0];
  };
} | {
  type: 'MARK_NOTIFICATION_READ';
  payload: string;
} | {
  type: 'ADD_NOTIFICATION';
  payload: Notification;
};
const initialState: AppState = {
  currentRole: 'Admin',
  language: 'en',
  currentView: 'dashboard',
  selectedRecordId: null,
  landRecords: initialLandRecords,
  notifications: initialNotifications,
  documents: allDocuments,
  filters: {},
  sidebarCollapsed: false,
  searchQuery: ''
};
function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_ROLE':
      return {
        ...state,
        currentRole: action.payload
      };
    case 'SET_LANGUAGE':
      return {
        ...state,
        language: action.payload
      };
    case 'SET_VIEW':
      return {
        ...state,
        currentView: action.payload,
        selectedRecordId: null
      };
    case 'SET_SELECTED_RECORD':
      return {
        ...state,
        selectedRecordId: action.payload
      };
    case 'SET_FILTERS':
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload
        }
      };
    case 'CLEAR_FILTERS':
      return {
        ...state,
        filters: {}
      };
    case 'TOGGLE_SIDEBAR':
      return {
        ...state,
        sidebarCollapsed: !state.sidebarCollapsed
      };
    case 'SET_SEARCH_QUERY':
      return {
        ...state,
        searchQuery: action.payload
      };
    case 'ADD_LAND_RECORD':
      return {
        ...state,
        landRecords: [...state.landRecords, action.payload]
      };
    case 'UPDATE_LAND_RECORD':
      return {
        ...state,
        landRecords: state.landRecords.map(r => r.id === action.payload.id ? action.payload : r)
      };
    case 'DELETE_LAND_RECORD':
      return {
        ...state,
        landRecords: state.landRecords.filter(r => r.id !== action.payload)
      };
    case 'ADD_DOCUMENT':
      return {
        ...state,
        documents: [...state.documents, action.payload]
      };
    case 'ADD_LITIGATION':
      return {
        ...state,
        landRecords: state.landRecords.map(r => r.id === action.payload.recordId ? {
          ...r,
          litigation: [...r.litigation, action.payload.litigation]
        } : r)
      };
    case 'MARK_NOTIFICATION_READ':
      return {
        ...state,
        notifications: state.notifications.map(n => n.id === action.payload ? {
          ...n,
          read: true
        } : n)
      };
    case 'ADD_NOTIFICATION':
      return {
        ...state,
        notifications: [action.payload, ...state.notifications]
      };
    default:
      return state;
  }
}
interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  navigate: (view: ViewType, recordId?: string) => void;
  canEdit: () => boolean;
  canDelete: () => boolean;
  canCreate: () => boolean;
  canAccessMasterData: () => boolean;
  canAccessUsersRoles: () => boolean;
}
const AppContext = createContext<AppContextType | undefined>(undefined);
export function AppProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const navigate = (view: ViewType, recordId?: string) => {
    dispatch({
      type: 'SET_VIEW',
      payload: view
    });
    if (recordId) {
      dispatch({
        type: 'SET_SELECTED_RECORD',
        payload: recordId
      });
    }
  };
  const canEdit = () => state.currentRole === 'Admin' || state.currentRole === 'Legal Officer';
  const canDelete = () => state.currentRole === 'Admin';
  const canCreate = () => state.currentRole === 'Admin' || state.currentRole === 'Legal Officer';
  const canAccessMasterData = () => state.currentRole === 'Admin';
  const canAccessUsersRoles = () => state.currentRole === 'Admin';
  return <AppContext.Provider value={{
    state,
    dispatch,
    navigate,
    canEdit,
    canDelete,
    canCreate,
    canAccessMasterData,
    canAccessUsersRoles
  }} data-id="element-85">
      {children}
    </AppContext.Provider>;
}
export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}